import React, { useState, useEffect, useRef } from 'react';
import { useStore } from '../context/ProductStore';

const Star = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? '#f59e0b' : 'none'} stroke="#f59e0b" strokeWidth="1.5">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const ProductDetail = ({ product, onClose }) => {
  const { addToCart } = useStore();

  const [activeImg,   setActiveImg]   = useState(0);
  const [added,       setAdded]       = useState(false);
  const [showAllSpec, setShowAllSpec] = useState(false);
  const [showVideo,   setShowVideo]   = useState(false);
  const [zoomLevel,   setZoomLevel]   = useState(1);
  const [panPos,      setPanPos]      = useState({ x: 50, y: 50 });
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);
  const imgBoxRef = useRef();

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const images   = product.images?.length ? product.images : (product.image ? [product.image] : []);
  const hasVideo = !!product.video;
  const discount = product.isPromo && product.promoPrice
    ? Math.round(((product.price - product.promoPrice) / product.price) * 100) : 0;
  const specs    = product.specs || [];
  const visSpecs = specs; // show all specs always
  const rating   = 4.5;
  const reviews  = Math.floor(Math.random() * 20) + 2;

  useEffect(() => {
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft')  { setActiveImg(i => (i-1+images.length)%images.length); setZoomLevel(1); }
      if (e.key === 'ArrowRight') { setActiveImg(i => (i+1)%images.length); setZoomLevel(1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length]);

  const handleMouseMove = (e) => {
    if (!imgBoxRef.current || zoomLevel <= 1) return;
    const r = imgBoxRef.current.getBoundingClientRect();
    setPanPos({
      x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
    });
  };

  const zoomIn  = () => setZoomLevel(z => Math.min(4, parseFloat((z + 0.5).toFixed(1))));
  const zoomOut = () => { const nz = parseFloat((zoomLevel - 0.5).toFixed(1)); setZoomLevel(Math.max(1, nz)); if (nz <= 1) setPanPos({ x: 50, y: 50 }); };
  const resetZoom = () => { setZoomLevel(1); setPanPos({ x: 50, y: 50 }); };

  const handleAddToCart = () => {
    addToCart(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=League+Spartan:wght@300;400;500;600;700;800;900&family=Inter:wght@400;500;600;700&display=swap');
        .pd-root * { font-family: 'League Spartan', sans-serif !important; box-sizing: border-box; }
        .pd-thumb:hover { border-color: #1a73e8 !important; }
        .pd-spec-row:hover td { background: #eef2f7 !important; }
        .pd-zoom-btn:hover { background: rgba(0,0,0,0.08) !important; }
        .pd-close-btn:hover { background: rgba(0,0,0,0.05) !important; }
        .pd-add-btn:hover { opacity: 0.88; }
        .pd-scroll::-webkit-scrollbar { width: 4px; }
        .pd-scroll::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 2px; }
        .pd-strip::-webkit-scrollbar { display: none; }
        .pd-video-btn { font-family: 'Inter', 'Segoe UI', system-ui, sans-serif !important; }
      `}</style>

      <div style={S.overlay} onClick={onClose}>
        <div style={S.modal} className="pd-root" onClick={e => e.stopPropagation()}>

          {/* TOP BAR */}
          <div style={S.topBar}>
            <nav style={S.breadcrumb}>
              <span style={S.breadLink} onClick={onClose}>HOME</span>
              <span style={S.breadSep}>/</span>
              <span style={S.breadLink}>{(product.category||'').toUpperCase()}</span>
              {product.brand && <>
                <span style={S.breadSep}>/</span>
                <span style={S.breadLink}>{(product.brand||'').toUpperCase()}</span>
              </>}
              <span style={S.breadSep}>/</span>
              <span style={S.breadCurrent}>{(product.name||'').toUpperCase()}</span>
            </nav>
            <button className="pd-close-btn" onClick={onClose} style={S.closeBtn}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* ══ MOBILE LAYOUT ══ */}
          {isMobile ? (
            <div style={{ overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch' }}>
              <div ref={imgBoxRef}
                style={{ ...S.mainImgBox, height: '280px', borderRadius: 0, border: 'none', borderBottom: '1px solid #f3f4f6' }}
                onMouseMove={handleMouseMove}>
                {images.length > 0 ? (
                  <img src={images[activeImg]} alt={product.name} draggable={false}
                    style={{ ...S.mainImg, transform: `scale(${zoomLevel})`, transformOrigin: `${panPos.x}% ${panPos.y}%`, transition: zoomLevel === 1 ? 'transform 0.3s' : 'none' }} />
                ) : <div style={S.noImg}>📦</div>}
                {product.isPromo && !product.outOfStock && <div style={S.promoBadge}>SAVE {discount}%</div>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={S.zoomToolbar}>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(zoomLevel !== 1)} onClick={resetZoom}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  </button>
                  <div style={S.toolbarDivider}/>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(zoomLevel > 1)} onClick={zoomOut} disabled={zoomLevel <= 1}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(true)} onClick={zoomIn} disabled={zoomLevel >= 4}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <div style={S.toolbarDivider}/>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(activeImg > 0)} onClick={() => { setActiveImg(i => Math.max(0, i-1)); resetZoom(); }} disabled={activeImg === 0}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(activeImg < images.length - 1)} onClick={() => { setActiveImg(i => Math.min(images.length-1, i+1)); resetZoom(); }} disabled={activeImg === images.length - 1}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>
              </div>

              {(images.length > 1 || hasVideo) && (
                <div className="pd-strip" style={{ overflowX: 'auto', display: 'flex', gap: '8px', padding: '12px 16px', borderBottom: '1px solid #f3f4f6', scrollbarWidth: 'none' }}>
                  {images.map((img, idx) => (
                    <div key={idx} className="pd-thumb"
                      onClick={() => { setActiveImg(idx); resetZoom(); }}
                      style={{ flexShrink: 0, width: '70px', height: '70px', border: `2.5px solid ${idx === activeImg ? '#1a73e8' : '#e5e7eb'}`, borderRadius: '8px', overflow: 'hidden', cursor: 'pointer', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}>
                      <img src={img} alt={`view ${idx+1}`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                  ))}
                  {hasVideo && (
                    <div onClick={() => setShowVideo(true)}
                      style={{ flexShrink: 0, width: '70px', height: '70px', border: '2px solid #fca5a5', borderRadius: '8px', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#CC0000"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </div>
                  )}
                </div>
              )}

              <div style={{ padding: '16px 16px 40px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={S.productNum}>Product # {product.id?.slice(0,8).toUpperCase() || 'TD-001'}</div>
                <div style={S.ratingRow}>
                  {[1,2,3,4,5].map(i => <Star key={i} filled={i <= Math.floor(rating)} />)}
                  <span style={S.ratingText}>{rating}/5 ({reviews})</span>
                </div>
                <h1 style={{ ...S.title, fontSize: '18px' }}>{product.name}</h1>
                {product.isPromo && product.promoPrice ? (
                  <div style={S.priceBlock}>
                    <div style={S.msrpLine}>
                      <span style={S.msrpLabel}>MSRP</span>
                      <span style={S.msrpOld}>USD {parseFloat(product.price).toFixed(2)}</span>
                      <span style={S.saveTag}>SAVE {discount}%</span>
                    </div>
                    <div style={{ ...S.mainPrice, fontSize: '28px' }}>USD {parseFloat(product.promoPrice).toFixed(2)}</div>
                  </div>
                ) : (
                  <div style={{ ...S.mainPrice, fontSize: '28px' }}>USD {parseFloat(product.price).toFixed(2)}</div>
                )}
                {!product.outOfStock ? (
                  <button className="pd-add-btn" onClick={handleAddToCart}
                    style={{ ...S.addBtn(added), width: '100%', padding: '15px', fontSize: '15px' }}>
                    {added ? '✅ ADDED TO CART!' : 'ADD TO CART'}
                  </button>
                ) : <div style={S.outOfStockBox}>Out of Stock</div>}
                <div style={S.stockRow}>
                  <div style={S.stockDot(!product.outOfStock)} />
                  <span style={S.stockText(!product.outOfStock)}>{product.outOfStock ? 'Out of Stock' : 'In Stock'}</span>
                </div>
                {hasVideo && (
                  <button className="pd-video-btn" onClick={() => setShowVideo(true)}
                    style={{ ...S.videoBtn, width: '100%', padding: '12px' }}>▶ Watch Product Video</button>
                )}
                {product.description && (
                  <div style={S.descSection}>
                    {product.description.split('.').filter(s=>s.trim()).slice(0,4).map((line,i)=>(
                      <div key={i} style={S.descItem}>
                        <span style={S.descDot}>•</span>
                        <span style={S.descText}>{line.trim()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {specs.length > 0 && (
                  <div style={S.specsSection}>
                    <div style={S.specsTitle}>SPECIFICATIONS</div>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        {visSpecs.map((s,i) => (
                          <tr key={i} className="pd-spec-row">
                            <td style={S.specKey}>{s.key}</td>
                            <td style={S.specVal}>{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {false && specs.length > 8 && (
                      <button onClick={() => setShowAllSpec(v=>!v)} style={S.seeMoreBtn}>
                        {showAllSpec ? '▲ Show fewer' : `▼ See all ${specs.length} specifications`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

          ) : (
            /* ══ DESKTOP LAYOUT ══ */
            <div style={{ display:'flex', width:'100%' }}>

              {/* Left — image */}
              <div style={S.leftCol}>
                <div ref={imgBoxRef} style={{ ...S.mainImgBox, height: '460px' }}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => zoomLevel <= 1 && setPanPos({ x: 50, y: 50 })}>
                  {images.length > 0 ? (
                    <img src={images[activeImg]} alt={product.name} draggable={false}
                      style={{ ...S.mainImg, transform: `scale(${zoomLevel})`, transformOrigin: zoomLevel > 1 ? `${panPos.x}% ${panPos.y}%` : 'center', transition: zoomLevel === 1 ? 'transform 0.3s' : 'none', cursor: zoomLevel > 1 ? 'crosshair' : 'default' }} />
                  ) : <div style={S.noImg}>📦</div>}
                  {product.isPromo && !product.outOfStock && <div style={S.promoBadge}>SAVE {discount}%</div>}
                </div>

                <div style={S.zoomToolbar}>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(zoomLevel !== 1)} onClick={resetZoom} title="Reset zoom">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                  </button>
                  <div style={S.toolbarDivider}/>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(zoomLevel > 1)} onClick={zoomOut} disabled={zoomLevel <= 1}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(true)} onClick={zoomIn} disabled={zoomLevel >= 4}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  </button>
                  <div style={S.toolbarDivider}/>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(activeImg > 0)} onClick={() => { setActiveImg(i => Math.max(0, i-1)); resetZoom(); }} disabled={activeImg === 0}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                  </button>
                  <button className="pd-zoom-btn" style={S.toolbarBtn(activeImg < images.length - 1)} onClick={() => { setActiveImg(i => Math.min(images.length-1, i+1)); resetZoom(); }} disabled={activeImg === images.length - 1}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                  </button>
                </div>

                {/* Thumbnails in left column */}
                {images.length > 1 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {images.map((img, idx) => (
                      <div key={idx} className="pd-thumb"
                        onClick={() => { setActiveImg(idx); resetZoom(); }}
                        style={{ width:64, height:64, border:`2px solid ${idx===activeImg?'#1a73e8':'#e5e7eb'}`, borderRadius:6, overflow:'hidden', cursor:'pointer', backgroundColor:'#f9fafb', display:'flex', alignItems:'center', justifyContent:'center', padding:4 }}>
                        <img src={img} alt={`view ${idx+1}`} style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
                      </div>
                    ))}
                    {hasVideo && (
                      <div onClick={() => setShowVideo(true)}
                        style={{ width:64, height:64, border:'2px solid #fca5a5', borderRadius:6, backgroundColor:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="#CC0000"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right — info */}
              <div style={S.rightCol} className="pd-scroll">
                <div style={S.productNum}>Product # {product.id?.slice(0,8).toUpperCase() || 'TD-001'}</div>
                <div style={S.ratingRow}>
                  {[1,2,3,4,5].map(i => <Star key={i} filled={i <= Math.floor(rating)} />)}
                  <span style={S.ratingText}>{rating}/5 ({reviews})</span>
                </div>
                <h1 style={S.title}>{product.name}</h1>

                {product.isPromo && product.promoPrice ? (
                  <div style={S.priceBlock}>
                    <div style={S.msrpLine}>
                      <span style={S.msrpLabel}>MSRP</span>
                      <span style={S.msrpOld}>USD {parseFloat(product.price).toFixed(2)}</span>
                      <span style={S.saveTag}>SAVE USD {(product.price - product.promoPrice).toFixed(2)} ({discount}%)</span>
                    </div>
                    <div style={S.mainPrice}>USD {parseFloat(product.promoPrice).toFixed(2)}</div>
                  </div>
                ) : (
                  <div style={S.mainPrice}>USD {parseFloat(product.price).toFixed(2)}</div>
                )}

                {!product.outOfStock ? (
                  <button className="pd-add-btn" onClick={handleAddToCart} style={S.addBtn(added)}>
                    {added ? '✅ ADDED TO CART!' : 'ADD TO CART'}
                  </button>
                ) : <div style={S.outOfStockBox}>Out of Stock</div>}

                <div style={S.stockRow}>
                  <div style={S.stockDot(!product.outOfStock)} />
                  <span style={S.stockText(!product.outOfStock)}>{product.outOfStock ? 'Out of Stock' : 'In Stock'}</span>
                </div>

                {hasVideo && (
                  <button className="pd-video-btn" onClick={() => setShowVideo(true)} style={S.videoBtn}>▶ Watch Product Video</button>
                )}

                {/* ── Specs table ── */}
                {specs.length > 0 && (
                  <div style={S.specsSection}>
                    <div style={S.specsTitle}>SPECIFICATIONS</div>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                      <tbody>
                        {visSpecs.map((s, i) => (
                          <tr key={i} className="pd-spec-row">
                            <td style={S.specKey}>{s.key}</td>
                            <td style={S.specVal}>{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {false && specs.length > 8 && (
                      <button onClick={() => setShowAllSpec(v=>!v)} style={S.seeMoreBtn}>
                        {showAllSpec ? '▲ Show fewer specifications' : `▼ See all ${specs.length} specifications`}
                      </button>
                    )}
                  </div>
                )}

                {product.description && (
                  <div style={S.descSection}>
                    {product.description.split('.').filter(s=>s.trim()).slice(0,4).map((line,i)=>(
                      <div key={i} style={S.descItem}>
                        <span style={S.descDot}>•</span>
                        <span style={S.descText}>{line.trim()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showVideo && hasVideo && (
        <div style={S.videoOverlay} onClick={() => setShowVideo(false)}>
          <div style={S.videoBox} onClick={e=>e.stopPropagation()}>
            <button style={S.videoClose} onClick={() => setShowVideo(false)}>✕</button>
            <p style={S.videoTitle}>{product.name}</p>
            <video src={product.video} controls autoPlay style={S.videoPlayer} />
          </div>
        </div>
      )}
    </>
  );
};

const S = {
  overlay:  { position:'fixed', inset:0, zIndex:99999, backgroundColor:'#fff', overflowY:'auto' },
  modal:    { backgroundColor:'#fff', width:'100%', color:'#111', display:'flex', flexDirection:'column' },

  topBar:      { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', borderBottom:'1px solid #e5e7eb', backgroundColor:'#fff', position:'sticky', top:0, zIndex:10, minHeight:'48px', flexShrink:0 },
  breadcrumb:  { display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', flex:1, minWidth:0, overflow:'hidden' },
  breadLink:   { color:'#6b7280', cursor:'pointer', fontWeight:'500', whiteSpace:'nowrap' },
  breadSep:    { color:'#d1d5db', flexShrink:0, margin:'0 2px' },
  breadCurrent:{ color:'#111', fontWeight:'700', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  closeBtn:    { width:'36px', height:'36px', borderRadius:'50%', border:'1.5px solid #e5e7eb', background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:'8px' },

  mainImgBox:  { display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'#ffffff', overflow:'hidden', width:'100%', position:'relative', border:'1px solid #f3f4f6', borderRadius:'8px' },
  mainImg:     { maxWidth:'80%', maxHeight:'80%', objectFit:'contain', userSelect:'none', display:'block', backgroundColor:'#ffffff' },
  noImg:       { fontSize:'72px' },
  promoBadge:  { position:'absolute', top:'12px', left:'12px', background:'#CC0000', color:'#fff', fontSize:'11px', fontWeight:'800', padding:'4px 10px', borderRadius:'100px' },

  zoomToolbar: { display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'rgba(240,240,240,0.9)', backdropFilter:'blur(8px)', borderRadius:'100px', padding:'5px 8px', gap:'2px', alignSelf:'center', border:'1px solid #e5e7eb', boxShadow:'0 2px 10px rgba(0,0,0,0.08)' },
  toolbarBtn:  (active) => ({ width:'38px', height:'38px', borderRadius:'50%', border:'none', background:'transparent', cursor:active?'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', color:active?'#374151':'#d1d5db', transition:'background 0.15s' }),
  toolbarDivider: { width:'1px', height:'22px', background:'#d1d5db', margin:'0 3px' },

  leftCol:  { flex:'0 0 55%', padding:'24px 40px', display:'flex', flexDirection:'column', gap:'16px', borderRight:'1px solid #e5e7eb' },
  rightCol: { flex:1, padding:'24px 28px 60px', display:'flex', flexDirection:'column', gap:'14px' },

  productNum: { fontSize:'12px', color:'#9ca3af', fontWeight:'500' },
  ratingRow:  { display:'flex', alignItems:'center', gap:'4px' },
  ratingText: { fontSize:'13px', color:'#374151', fontWeight:'600', marginLeft:'4px' },
  title:      { fontSize:'clamp(16px,2vw,22px)', fontWeight:'700', color:'#111', lineHeight:1.35, margin:0 },

  priceBlock: { display:'flex', flexDirection:'column', gap:'4px' },
  msrpLine:   { display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' },
  msrpLabel:  { fontSize:'11px', fontWeight:'800', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.08em' },
  msrpOld:    { fontSize:'13px', color:'#9ca3af', textDecoration:'line-through', fontWeight:'500' },
  saveTag:    { fontSize:'12px', fontWeight:'800', color:'#CC0000' },
  mainPrice:  { fontSize:'clamp(24px,2.5vw,32px)', fontWeight:'900', color:'#111', letterSpacing:'-0.02em' },

  addBtn:     (added) => ({ padding:'14px 20px', borderRadius:'6px', border:'none', background:added?'#16a34a':'#1a73e8', color:'#fff', fontWeight:'800', fontSize:'14px', cursor:'pointer', letterSpacing:'0.08em', fontFamily:'inherit', transition:'opacity 0.2s', width:'100%' }),
  outOfStockBox: { padding:'12px', borderRadius:'6px', background:'#f3f4f6', color:'#9ca3af', fontWeight:'700', fontSize:'14px', textAlign:'center' },

  stockRow:  { display:'flex', alignItems:'center', gap:'6px' },
  stockDot:  (ok) => ({ width:'9px', height:'9px', borderRadius:'50%', background:ok?'#22c55e':'#ef4444', boxShadow:`0 0 5px ${ok?'#22c55e':'#ef4444'}` }),
  stockText: (ok) => ({ fontSize:'13px', fontWeight:'700', color:ok?'#16a34a':'#dc2626' }),

  videoBtn:   { padding:'10px 16px', borderRadius:'6px', border:'1.5px solid #CC0000', background:'transparent', color:'#CC0000', fontWeight:'600', fontSize:'16px', cursor:'pointer', fontFamily:"'Inter', 'Segoe UI', system-ui, sans-serif", width:'100%' },

  descSection: { display:'flex', flexDirection:'column', gap:'6px' },
  descItem:    { display:'flex', gap:'8px', alignItems:'flex-start' },
  descDot:     { color:'#6b7280', flexShrink:0, fontSize:'16px', marginTop:'1px' },
  descText:    { fontSize:'14px', color:'#374151', lineHeight:1.6, fontFamily:"'Inter', 'Segoe UI', system-ui, sans-serif", fontWeight:'400' },

  /* ── Specs table — matches screenshot exactly ── */
  specsSection: { display:'flex', flexDirection:'column', border:'1px solid #d0d5dd', borderRadius:'4px', overflow:'hidden', marginTop:4 },
  specsTitle:   { fontSize:'11px', fontWeight:'800', letterSpacing:'0.1em', color:'#374151', textTransform:'uppercase', padding:'10px 16px', background:'#f0f2f5', borderBottom:'1px solid #d0d5dd' },
  specKey:      { fontSize:'13px', fontWeight:'500', color:'#374151', width:'160px', padding:'11px 16px', backgroundColor:'#f0f2f5', borderRight:'1px solid #d0d5dd', borderBottom:'1px solid #e5e7eb', verticalAlign:'middle', lineHeight:1.4 },
  specVal:      { fontSize:'13px', color:'#111', fontWeight:'400', padding:'11px 16px', backgroundColor:'#fff', borderBottom:'1px solid #e5e7eb', verticalAlign:'middle', lineHeight:1.5 },
  seeMoreBtn:   { padding:'10px 16px', border:'none', background:'#f0f2f5', color:'#1a73e8', fontWeight:'700', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', textAlign:'left', borderTop:'1px solid #d0d5dd', width:'100%' },

  videoOverlay: { position:'fixed', inset:0, zIndex:999999, backgroundColor:'rgba(0,0,0,0.88)', display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' },
  videoBox:     { background:'#111', borderRadius:'14px', padding:'20px', maxWidth:'720px', width:'100%', position:'relative', display:'flex', flexDirection:'column', gap:'12px' },
  videoClose:   { position:'absolute', top:'-12px', right:'-12px', background:'#CC0000', border:'none', color:'#fff', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontSize:'15px', fontWeight:'700', display:'flex', alignItems:'center', justifyContent:'center' },
  videoTitle:   { color:'#fff', fontSize:'15px', fontWeight:'700', margin:0 },
  videoPlayer:  { width:'100%', maxHeight:'60vh', borderRadius:'10px', backgroundColor:'#000' },
};

export default ProductDetail;
